import { ADMIN_ROLE, SALES_ROLES, ADMIN_WORKSPACE_ROLES } from '../config/roles';
import { LOI_STAGE_UPDATE, AGREEMENT_STAGE_UPDATE } from '../constants/permissions';

export { ADMIN_WORKSPACE_ROLES };

/** Map sidebar page-keys → routes (for post-login redirect). */
const PAGE_PATHS = {
    'admin.dashboard': '/admin/dashboard',
    'admin.kpi-analytics': '/admin/kpi-analytics',
    'admin.approvals': '/admin/approvals',
    'admin.users': '/admin/users',
    'admin.access': '/admin/access',
    'admin.clients': '/admin/clients',
    'admin.payment-ledger': '/admin/payment-ledger',
    'admin.form-builder': '/admin/form-builder',
    'admin.downloads': '/admin/downloads',
    'admin.achievers': '/admin/achievers',
    'admin.hero-slides': '/admin/hero-slides',
    'admin.franchises': '/admin/franchises',
    'admin.incentives': '/admin/incentives',
    'admin.refunds': '/admin/refunds',
    'admin.reimbursements': '/admin/reimbursements',
    'admin.zoho-leads': '/admin/zoho-leads',
    'admin.aoi-calculator': '/admin/aoi-calculator',
    'admin.aoi-location': '/admin/aoi-location',
    'admin.activity': '/admin/activity',
    'admin.settings': '/admin/settings',
    'sales.dashboard': '/sales/dashboard',
    'sales.kpi-analytics': '/sales/kpi-analytics',
    'sales.leads': '/sales/leads',
    'sales.team-leads': '/sales/team-leads',
    'sales.my-team': '/sales/my-team',
    'sales.clients': '/sales/clients',
    'sales.clients.new': '/sales/clients/new',
    'sales.legal': '/sales/legal',
    'sales.downloads': '/sales/downloads',
    'sales.incentives': '/sales/incentives',
    'sales.reimbursements': '/sales/reimbursements',
};

/**
 * Effective page-keys for this user.
 * Empty user.permissions → inherit role template exactly.
 * Non-empty user.permissions → per-user override (replaces role, set in Access UI).
 */
export function effectivePermissions(user, roleDoc) {
    const userPerms = (user?.permissions || []).filter(Boolean);
    if (userPerms.length) return userPerms;
    return (roleDoc?.permissions || []).filter(Boolean);
}

export function pageVariant(pageKey) {
    return typeof pageKey === 'string' && pageKey.startsWith('sales.') ? 'sales' : 'admin';
}

function firstPathForPrefix(perms, prefix) {
    for (const key of perms) {
        if (key.startsWith(prefix) && PAGE_PATHS[key]) return PAGE_PATHS[key];
    }
    return null;
}

export function usesAdminWorkspace(user, roleDoc = null) {
    if (!user) return false;
    if (user.role === ADMIN_ROLE) return true;
    if (roleDoc?.isSuperUser) return true;
    /* Staff-console roles use /admin layout — matches backend hasAdminConsoleAccess. */
    if (ADMIN_WORKSPACE_ROLES.has(user.role)) return true;
    return effectivePermissions(user, roleDoc).some(p => typeof p === 'string' && p.startsWith('admin.'));
}

/** Sales sidebar layout (/sales/*). */
export function usesSalesWorkspace(user, roleDoc = null) {
    if (!user) return false;
    if (SALES_ROLES.has(user.role)) return true;
    return effectivePermissions(user, roleDoc).some(p => typeof p === 'string' && p.startsWith('sales.'));
}

/** Whether the marketing landing page at `/` is available to this user. */
export function landingPageVisible(user, features = {}) {
    if (features?.landingPage === false) {
        return user?.role === ADMIN_ROLE || user?.role === 'manager';
    }
    return true;
}

/** Post-login / onboarding-complete landing path. */
export function homePathForUser(user, roleDoc = null) {
    if (!user) return '/login';
    if (user.onboardingStep && user.onboardingStep !== 'complete') {
        switch (user.onboardingStep) {
            case 'mail_otp': return '/onboarding/verify-email';
            case 'admin_verify': return '/onboarding/pending-approval';
            case 'kra_accept': return '/onboarding/kra';
            case 'kpi_accept': return '/onboarding/kpi';
            case 'training': return '/onboarding/training';
            case 'welcome': return '/onboarding/welcome';
            default: break;
        }
    }

    const perms = effectivePermissions(user, roleDoc);

    if (usesAdminWorkspace(user, roleDoc)) {
        if (permissionAllows(perms, 'admin.dashboard')) return PAGE_PATHS['admin.dashboard'];
        const adminPath = firstPathForPrefix(perms, 'admin.');
        if (adminPath) return adminPath;
        /* Role template not loaded on client yet — staff roles still land on admin dashboard. */
        return PAGE_PATHS['admin.dashboard'];
    }
    if (SALES_ROLES.has(user.role) || usesSalesWorkspace(user, roleDoc)) {
        if (permissionAllows(perms, 'sales.dashboard')) return PAGE_PATHS['sales.dashboard'];
        return firstPathForPrefix(perms, 'sales.') || PAGE_PATHS['sales.dashboard'];
    }
    return '/';
}

/** Sidebar/API page-key aliases (legacy keys from permissions UI). */
export const PERM_ALIASES = {
    'admin.access': ['admin.permissions'],
    'admin.permissions': ['admin.access'],
};

export function permissionAllows(allowList, pageKey) {
    const set = new Set(allowList || []);
    if (set.has(pageKey)) return true;
    for (const alt of PERM_ALIASES[pageKey] || []) {
        if (set.has(alt)) return true;
    }
    return false;
}

/** Whether a sidebar page-key is allowed — strict match to granted keys only. */
export function canAccessPage(user, roleDoc, pageKey) {
    if (!user) return false;
    if (user.role === ADMIN_ROLE || roleDoc?.isSuperUser) return true;

    const perms = effectivePermissions(user, roleDoc);
    if (!perms.length) return false;

    /* One dashboard: admin dashboard takes precedence over sales dashboard. */
    if (pageKey === 'sales.dashboard' && permissionAllows(perms, 'admin.dashboard')) return false;

    if (permissionAllows(perms, pageKey)) return true;
    if (pageKey === 'admin.payment-ledger' && permissionAllows(perms, 'admin.clients')) return true;
    /* Legal ships with the client workflow — show when My clients is granted, or for standard sales_user roles. */
    if (pageKey === 'sales.legal') {
        if (permissionAllows(perms, 'sales.clients')) return true;
        if (user.role === 'sales_user') return true;
    }
    return false;
}

/** BDM-only nav items — require BDM designation AND the page permission. */
export function canSeeBdmOnlyPage(user, roleDoc, pageKey) {
    if (user?.salesDesignation !== 'bdm') return false;
    return canAccessPage(user, roleDoc, pageKey);
}

/** Whether the user may update LOI stage on a client (admin-granted action permission). */
export function canUpdateLoiStage(user, roleDoc) {
    if (!user) return false;
    if (user.role === ADMIN_ROLE || roleDoc?.isSuperUser) return true;
    return effectivePermissions(user, roleDoc).includes(LOI_STAGE_UPDATE);
}

/** Whether the user may update agreement stage on a client (admin-granted action permission). */
export function canUpdateAgreementStage(user, roleDoc) {
    if (!user) return false;
    if (user.role === ADMIN_ROLE || roleDoc?.isSuperUser) return true;
    return effectivePermissions(user, roleDoc).includes(AGREEMENT_STAGE_UPDATE);
}
